import React from 'react';
import { Link } from 'react-router-dom';
import { Building2, BookOpen, Users, Utensils, Heart, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

const CausesPage = () => {
  const religiousCauses = [
    {
      icon: Building2,
      title: 'Temple Construction',
      description: 'Building sacred spaces where devotees can worship, meditate, and experience divine blessings. Each temple becomes a center for spiritual growth and community gatherings.',
      image: 'https://images.pexels.com/photos/15902534/pexels-photo-15902534.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
    },
    {
      icon: '🛕',
      title: 'Lord Ram Temple Project',
      description: 'Our flagship project dedicated to Lord Ram. This magnificent temple will serve as a spiritual beacon for millions of devotees and a symbol of our cultural heritage.',
      image: 'https://images.pexels.com/photos/15902873/pexels-photo-15902873.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
      featured: true,
    },
    {
      icon: '🏛️',
      title: 'Temple Renovation',
      description: 'Restoring and preserving ancient temples that are in urgent need of renovation. Protecting our architectural heritage for future generations.',
      image: 'https://images.pexels.com/photos/15902873/pexels-photo-15902873.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
    },
    {
      icon: '📿',
      title: 'Cultural Preservation',
      description: 'Documenting and preserving Hindu traditions, rituals, and practices. Organizing cultural events and festivals to keep our heritage alive.',
      image: 'https://images.pexels.com/photos/15902534/pexels-photo-15902534.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
    },
  ];

  const socialCauses = [
    {
      icon: Users,
      title: 'Women Empowerment',
      description: 'Empowering women through skill development, vocational training, and self-help groups. Creating opportunities for financial independence and self-reliance.',
      image: 'https://images.pexels.com/photos/6753336/pexels-photo-6753336.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
    },
    {
      icon: BookOpen,
      title: 'Student Scholarships',
      description: 'Providing quality education to underprivileged students through scholarships covering fees, books, and educational materials. Investing in tomorrow\'s leaders.',
      image: 'https://images.pexels.com/photos/14325738/pexels-photo-14325738.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
    },
    {
      icon: Utensils,
      title: 'Food Donation Programs',
      description: 'Daily Annadanam (food donation) programs at temples and for the needy. No one should go hungry - this is our commitment to serving prasadam.',
      image: 'https://images.pexels.com/photos/6995247/pexels-photo-6995247.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
    },
    {
      icon: Heart,
      title: 'Community Support',
      description: 'Medical camps, disaster relief, and community welfare programs. Standing with families during times of need and helping communities thrive.',
      image: 'https://images.pexels.com/photos/6753336/pexels-photo-6753336.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
    },
  ];

  return (
    <div className="min-h-screen bg-cream" data-testid="causes-page">
      {/* Hero */}
      <section className="relative bg-terracotta py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="font-body text-sm uppercase tracking-wider text-saffron font-semibold">Our Causes</span>
            <h1 className="font-heading text-4xl md:text-6xl text-white mt-4 mb-6">
              Serving Dharma & Humanity
            </h1>
            <p className="font-body text-lg text-white/80 leading-relaxed">
              Our work spans across religious and social welfare activities, each designed to preserve our heritage and uplift communities.
            </p>
          </div>
        </div>
      </section>

      {/* Religious & Cultural Causes */}
      <section className="py-20 bg-white" data-testid="religious-causes-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="font-body text-sm uppercase tracking-wider text-saffron font-semibold">Dharma</span>
            <h2 className="font-heading text-3xl md:text-4xl text-gray-900 mt-4">Religious & Cultural Activities</h2>
            <p className="font-body text-gray-600 mt-4 max-w-2xl mx-auto">
              Building and preserving sacred spaces for spiritual growth and cultural continuity.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {religiousCauses.map((cause, index) => (
              <Card 
                key={index} 
                className={`bg-cream rounded-2xl border-none overflow-hidden hover:shadow-lg transition-all ${cause.featured ? 'lg:col-span-2' : ''}`}
              >
                <div className={`grid ${cause.featured ? 'grid-cols-1 lg:grid-cols-2' : 'grid-cols-1'}`}>
                  <div className="img-zoom">
                    <img 
                      src={cause.image} 
                      alt={cause.title}
                      className={`w-full ${cause.featured ? 'h-64 lg:h-full' : 'h-48'} object-cover`}
                    />
                  </div>
                  <CardContent className="p-8 flex flex-col justify-center">
                    {cause.featured && (
                      <span className="inline-flex items-center gap-2 px-3 py-1 bg-saffron/10 text-saffron rounded-full text-xs font-semibold mb-4 w-fit">
                        Featured Project
                      </span>
                    )}
                    <div className="flex items-center gap-3 mb-4">
                      {typeof cause.icon === 'string' ? (
                        <span className="text-3xl">{cause.icon}</span>
                      ) : (
                        <div className="w-12 h-12 bg-saffron/10 rounded-full flex items-center justify-center">
                          <cause.icon className="w-6 h-6 text-saffron" />
                        </div>
                      )}
                      <h3 className="font-heading text-2xl text-gray-900">{cause.title}</h3>
                    </div>
                    <p className="font-body text-gray-600 leading-relaxed mb-6">{cause.description}</p>
                    <Link to="/donate">
                      <Button className="bg-saffron hover:bg-saffron-dark text-white rounded-full px-6 font-body w-fit">
                        Support This Cause <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                    </Link>
                  </CardContent>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Social Welfare Causes */}
      <section className="py-20 bg-cream" data-testid="social-causes-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="font-body text-sm uppercase tracking-wider text-saffron font-semibold">Seva</span>
            <h2 className="font-heading text-3xl md:text-4xl text-gray-900 mt-4">Social Welfare Activities</h2>
            <p className="font-body text-gray-600 mt-4 max-w-2xl mx-auto">
              Empowering individuals and communities through education, healthcare, and support services.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {socialCauses.map((cause, index) => (
              <Card key={index} className="bg-white rounded-2xl border-none overflow-hidden hover:shadow-lg transition-all card-hover">
                <div className="img-zoom">
                  <img 
                    src={cause.image} 
                    alt={cause.title}
                    className="w-full h-48 object-cover"
                  />
                </div>
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-terracotta/10 rounded-full flex items-center justify-center">
                      <cause.icon className="w-6 h-6 text-terracotta" />
                    </div>
                    <h3 className="font-heading text-xl text-gray-900">{cause.title}</h3>
                  </div>
                  <p className="font-body text-gray-600 text-sm leading-relaxed mb-4">{cause.description}</p>
                  <Link to="/donate">
                    <Button variant="outline" className="border-terracotta text-terracotta hover:bg-terracotta hover:text-white rounded-full px-6 font-body">
                      Donate Now
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-saffron">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl md:text-4xl text-white mb-6">Ready to Make a Difference?</h2>
          <p className="font-body text-lg text-white/90 mb-8">
            Choose a cause close to your heart and contribute to building a better tomorrow.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/donate">
              <Button className="bg-white text-saffron hover:bg-cream rounded-full px-8 py-6 font-body font-semibold text-lg btn-lift">
                Donate Now
              </Button>
            </Link>
            <Link to="/volunteer">
              <Button variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-saffron rounded-full px-8 py-6 font-body font-semibold text-lg transition-all">
                Volunteer With Us
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CausesPage;
